import { useState, useCallback } from 'react';

/**
 * Local toast state interface
 * Used for rendering toast notifications inside modals
 */
export interface LocalToastState {
  visible: boolean;
  type: 'success' | 'error';
  message: string;
}

const INITIAL_STATE: LocalToastState = {
  visible: false,
  type: 'success',
  message: '',
};

/**
 * Custom hook for managing local toast state
 * 
 * Use this hook when you need to show toast notifications inside modals
 * where the global toast context won't work due to Modal z-index issues.
 * 
 * @example
 * ```tsx
 * const { toast, showToast, hideToast, resetToast } = useLocalToast();
 * 
 * // Show success toast
 * showToast('success', 'Operation completed!');
 * 
 * // Show error toast
 * showToast('error', 'Something went wrong');
 * 
 * // In render
 * <Toast
 *   type={toast.type}
 *   message={toast.message}
 *   visible={toast.visible}
 *   onHide={hideToast}
 *   autoHide={true}
 *   duration={4000}
 *   position="top"
 * />
 * ```
 */
export const useLocalToast = () => {
  const [toast, setToast] = useState<LocalToastState>(INITIAL_STATE);

  /**
   * Show a toast notification
   * @param type - 'success' or 'error'
   * @param message - Message to display
   */
  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ visible: true, type, message });
  }, []);

  /**
   * Hide the current toast (for animation completion)
   */
  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, visible: false }));
  }, []);

  /**
   * Reset toast to initial state (use when closing modal)
   */
  const resetToast = useCallback(() => {
    setToast(INITIAL_STATE);
  }, []);

  return {
    toast,
    showToast,
    hideToast,
    resetToast,
  };
};

export default useLocalToast;
